import React, { useEffect, useState, useRef, useCallback } from 'react'
import { observer } from 'mobx-react'
import { Link, useNavigate } from 'react-router'

// evertel
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { LoginService } from '@evertel/session/LoginService'
import { AuthenticationController } from '@evertel/shared/feature-authentication-auth0'
import { Button, Icon, Card, CardBody, Col, Row, Input, InfoBox, CardFooter, useUI } from '@evertel/web/ui'


const Confirm2FAPage = observer(() => {

    const analytics = useService(Analytics, [])
    const loginService = useService(LoginService, [])
    const authenticationController = useService(AuthenticationController, [])

    const autoSubmitCountRef = useRef(0)

    const navigate = useNavigate()

    const { addToast } = useUI()

    const [code, setCode] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isResending, setIsResending] = useState(false)
    const [error, setError] = useState('')
    const [showMoreWays, setShowMoreWays] = useState(false)
    const [activeAuthenticator, setActiveAuthenticator] = useState('email')

    useEffect(() => {
        analytics.logView('/two-factor')
    }, [])

    useEffect(() => {
        if (!loginService.loggedEmail) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: 'Missing email address.'
            })

            navigate('/login')
            return
        }

        (async () => {
            await authenticationController.getAuthenticatorsByEmail(loginService.loggedEmail)
            setActiveAuthenticator(authenticationController.hasSMSAuthenticator ? 'sms' : 'email')
        })()
    }, [])

    // Auto-submit when 6 digits entered
    useEffect(() => {
        if (code?.length === 6 && autoSubmitCountRef.current < 1) {
            autoSubmitCountRef.current++
            submitVerifyCode()
        }
    }, [code])

    const onChangeCode = (value: string) => {
        if (!isLoading) {
            setCode(value.replace(/\D/g, ''))
        }
    }

    const submitVerifyCode = useCallback(async (e?: React.FormEvent) => {
        if (e) e.preventDefault()

        setError('')

        if (!code) {
            setError('Please enter your security code')
            return
        }

        setIsLoading(true)

        try {
            await loginService.verifyMFA(code)
            // Clear any stale login callback (e.g., from registration flow
            // that was interrupted by 2FA). Matches mobile Confirm2FAScreen.
            loginService.setOnLoginCallback(undefined)
            navigate('/landing')
        } catch (err: any) {
            setError(err.message)
        } finally {
            setIsLoading(false)
        }
    }, [code, loginService, navigate])

    const resend2fa = async (useEmail = true, usePhone = false) => {
        if (!loginService.isMFAInProgress) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: 'Missing 2FA Token, please login again.'
            })

            setError('Resend failed [Missing 2FA Token]')
            navigate('/login')
            return
        }

        setIsResending(true)
        setActiveAuthenticator(usePhone ? 'sms' : 'email')

        try {
            const options = {
                email: loginService.loggedEmail,
                useEmail,
                usePhone
            }

            await loginService.challengeMFA(options)

            addToast({
                title: 'Success',
                color: 'success',
                message: 'A new security code has been sent!'
            })
        } catch (err: any) {
            setError(err.message)
        } finally {
            setIsResending(false)
        }
    }

    const toggleMoreWays = () => {
        setShowMoreWays(!showMoreWays)
    }

    const disableResendButtons = isLoading || isResending
    const email = !authenticationController.isLoading ? authenticationController.authenticators?.find(a => a.type === 'email')?.name : null
    const phoneNumber = !authenticationController.isLoading ? authenticationController.authenticators?.find(a => a.type === 'sms')?.name || null : null

    return (
        <div className="app fadeIn animated auth-layout" style={{ height: '100vh', overflow: 'auto' }}>
            <Row align="center" valign="center" className="p-4" style={{ minHeight: '100%' }}>
                <Card className="" style={{ maxWidth: 700 }}>
                    <CardBody className="p-4">
                        {/* HEADER */}
                        <Row className="mb-3">
                            <Col className="text-center" align="center">
                                <h1>Two-Step Verification</h1>
                                <h3>
                                    For your protection, we've sent you a security code to verify it's you.
                                </h3>
                            </Col>
                        </Row>
                        {error && (
                            <InfoBox color="danger" className="mt-2">
                                {error}
                            </InfoBox>
                        )}
                        <Row align="center" valign="center" className="text-primary">
                            <Icon
                                name={(activeAuthenticator === 'email') ? 'envelope' : 'mobile-android-alt'}
                                className="me-2"
                                size={30}
                            />
                            <span>
                                {(activeAuthenticator === 'email') ? email : phoneNumber}
                            </span>
                        </Row>
                        <form onSubmit={submitVerifyCode}>
                            <div className="mt-2" style={{maxWidth: 200, margin: 'auto'}}>
                                <Input
                                    className="two-factor-input text-center"
                                    style={{
                                        letterSpacing: 10,
                                        fontSize: 'x-large',
                                        fontWeight: 'bold'
                                    }}
                                    name="securityCode"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={code}
                                    onChange={(e) => onChangeCode(e.target.value)}
                                    maxLength={6}
                                    placeholder="••••••"
                                    onKeyDown={(e) => {
                                        if (
                                            !/[\d]/.test(e.key)
                                                && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)
                                                && !e.ctrlKey
                                                && !e.metaKey
                                        ) {
                                            e.preventDefault()
                                        }
                                    }}
                                />
                                <Button
                                    color="primary"
                                    loading={isLoading}
                                    type="submit"
                                    className="mt-4 w-100"
                                >
                                    <strong>Verify</strong>
                                </Button>
                            </div>
                            <Row>
                                <Col align="center">
                                    <Button
                                        color="link"
                                        loading={isResending}
                                        disabled={disableResendButtons}
                                        type="button"
                                        className="mt-3"
                                        onClick={() => resend2fa(activeAuthenticator === 'email', activeAuthenticator === 'sms')}>
                                        Resend security code
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                        {!!phoneNumber && (
                            <>
                                <Row className="mt-3">
                                    <Col align="center">
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="mt-4"
                                            onClick={toggleMoreWays}>
                                            More ways to verify
                                        </Button>
                                    </Col>
                                </Row>
                                {showMoreWays && (
                                    <>
                                        <Row>
                                            <Col align="center">
                                                <Button
                                                    color="link"
                                                    loading={isResending && activeAuthenticator === 'email'}
                                                    disabled={disableResendButtons}
                                                    type="button"
                                                    size="sm"
                                                    onClick={() => resend2fa(true, false)}>
                                                    <Icon
                                                        name="envelope"
                                                        className="me-2"
                                                    />
                                                    Email code to: <strong>{email}</strong>
                                                </Button>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col align="center">
                                                <Button
                                                    color="link"
                                                    loading={isResending && activeAuthenticator === 'sms'}
                                                    disabled={disableResendButtons}
                                                    type="button"
                                                    size="sm"
                                                    onClick={() => resend2fa(false, true)}>
                                                    <Icon
                                                        name="mobile-android-alt"
                                                        className="me-2"
                                                    />
                                                    Text code to: <strong>{phoneNumber}</strong>
                                                </Button>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </>
                        )}
                    </CardBody>
                    <CardFooter>
                        <Row>
                            <Col align="center">
                                <div><small>Have an issue? We can help</small></div>
                                <a href="mailto:EvertelSupport@genasys.com" className="d-block btn btn-link">
                                    <small>EvertelSupport@genasys.com</small>
                                </a>
                                <Link
                                    className="mt-4"
                                    to="/login"
                                >
                                    <small>Logout</small>
                                </Link>
                            </Col>
                        </Row>
                    </CardFooter>
                </Card>
            </Row>
        </div>
    )
})

export { Confirm2FAPage }
